import NotificationBell from "@/components/NotificationBell";
import GoogleStatusAlert from "@/components/GoogleStatusAlert";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Map, Building2, AlertTriangle, LayoutDashboard, Shield, LogOut, Menu, User, Search, Moon, Sun, FileSignature, Wrench } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import CommandPalette from "@/components/CommandPalette";
import { ROLE_LABELS, BM_ROLES, ADMIN_ROLES, ALL_ROLES } from "@/lib/constants";

const nav = [
  { to: "/", icon: Map, label: "Carte", testid: "nav-map", roles: ALL_ROLES },
  { to: "/apartments", icon: Building2, label: "Appartements", testid: "nav-apartments", roles: ALL_ROLES },
  { to: "/problems", icon: AlertTriangle, label: "Anomalies", testid: "nav-problems", roles: ALL_ROLES },
  { to: "/maintenance", icon: Wrench, label: "Maintenance", testid: "nav-maintenance", roles: BM_ROLES },
  { to: "/baux", icon: FileSignature, label: "Gestion des BM", testid: "nav-baux", roles: BM_ROLES },
  { to: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord", testid: "nav-dashboard", roles: ALL_ROLES },
  { to: "/admin", icon: Shield, label: "Administration", testid: "nav-admin", roles: ADMIN_ROLES },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const visibleNav = nav.filter((n) => n.roles.includes(user?.role));
  const mobileBottomNav = ["/", "/apartments", "/problems", BM_ROLES.includes(user?.role) ? "/baux" : "/dashboard"]
    .map((to) => visibleNav.find((n) => n.to === to))
    .filter(Boolean);
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [bellHidden, setBellHidden] = useState(false);

  useEffect(() => {
    const onPanel = (e) => setBellHidden(!!e.detail);
    window.addEventListener("apt-panel-toggle", onPanel);
    return () => window.removeEventListener("apt-panel-toggle", onPanel);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside data-testid="sidebar" className="hidden md:flex fixed inset-y-0 left-0 w-16 hover:w-60 bg-[#0A1C36] text-slate-100 flex-col z-40 group transition-[width] duration-300 overflow-hidden">
        <div className="px-3 group-hover:px-5 py-5 flex items-center gap-2.5 border-b border-slate-800 justify-center group-hover:justify-start">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
            <img src="/logo.png" alt="HSH Ops" className="w-7 h-7 object-contain" />
          </div>
          <div className="hidden group-hover:block whitespace-nowrap">
            <div className="font-bold font-display text-white text-base leading-tight">HSH Ops</div>
            <div className="text-[11px] text-slate-400">Gestion opérationnelle</div>
          </div>
        </div>
        <button onClick={() => setPaletteOpen(true)} data-testid="global-search-btn"
          className="mx-2.5 group-hover:mx-3 mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 text-sm transition-colors justify-center group-hover:justify-start">
          <Search className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 text-left hidden group-hover:block whitespace-nowrap">Rechercher…</span>
          <kbd className="text-[10px] border border-slate-600 rounded px-1 hidden group-hover:inline">⌘K</kbd>
        </button>
        <nav className="flex-1 px-2.5 group-hover:px-3 py-3 space-y-1 thin-scroll overflow-y-auto overflow-x-hidden">
          {visibleNav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors justify-center group-hover:justify-start ${
                  isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}>
              <n.icon className="w-4 h-4 flex-shrink-0" /><span className="hidden group-hover:inline whitespace-nowrap">{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn" className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800 text-left justify-center group-hover:justify-start">
                <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-slate-200" />
                </div>
                <div className="min-w-0 flex-1 hidden group-hover:block">
                  <div className="text-sm font-medium text-white truncate">{user?.name || "Utilisateur"}</div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {user?.role === "admin"
                      ? (ROLE_LABELS[user?.role] || user?.role)
                      : (ROLE_LABELS[user?.role]?.split(" — ")[1] || user?.role)}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56 z-[1100]">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {ADMIN_ROLES.includes(user?.role) && (
                <DropdownMenuItem onClick={() => navigate("/admin")}>Profil</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setDark(!dark)} data-testid="theme-toggle">
                {dark ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {dark ? "Mode clair" : "Mode sombre"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={logout}>
                <LogOut className="w-4 h-4 mr-2" />Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className={`hidden md:block fixed top-4 right-5 z-40 ${bellHidden ? "invisible" : ""}`} data-testid="floating-bell">
        <NotificationBell />
      </div>

      {/* Mobile top */}
      <header className="md:hidden fixed top-0 inset-x-0 bg-white border-b border-slate-200 z-40 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="HSH Ops" className="w-6 h-6 object-contain" />
          </div>
          <div className="font-bold font-display text-slate-900">HSH Ops</div>
        </div>
        <div className="flex items-center">
          <button onClick={() => setPaletteOpen(true)} data-testid="mobile-search-btn" className="p-2">
            <Search className="w-5 h-5 text-slate-700" />
          </button>
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="mobile-menu-btn" className="p-2">
                <Menu className="w-5 h-5 text-slate-700" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[1100]">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/dashboard")}>Tableau de bord</DropdownMenuItem>
              {BM_ROLES.includes(user?.role) && (
                <DropdownMenuItem onClick={() => navigate("/baux")}>Gestion des BM</DropdownMenuItem>
              )}
              {ADMIN_ROLES.includes(user?.role) && (
                <DropdownMenuItem onClick={() => navigate("/admin")}>Administration</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setDark(!dark)} data-testid="theme-toggle-mobile">
                {dark ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {dark ? "Mode clair" : "Mode sombre"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout}><LogOut className="w-4 h-4 mr-2" />Déconnexion</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Content */}
      <main className="md:pl-16 pt-14 md:pt-0 pb-16 md:pb-0 min-h-screen relative">
        <div className="app-wallpaper" aria-hidden="true" />
        <div className="relative z-10">
          <div className="px-4 md:px-8 pt-4 md:pt-6"><GoogleStatusAlert /></div>
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-40 grid grid-cols-4">
        {mobileBottomNav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`mobile-${n.testid}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2.5 text-[11px] font-medium ${
                isActive ? "text-slate-900" : "text-slate-500"
              }`}>
            <n.icon className="w-5 h-5 mb-0.5" />{n.label}
          </NavLink>
        ))}
      </nav>

      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
    </div>
  );
}
